import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { StockTakeService } from '../stock-take.service';
import { StockTakeSession, StockTakeStatus } from '../stock-take.models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { UsersService } from '../../users/users.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-stock-take-details',
  templateUrl: './stock-take-details.component.html',
  styleUrls: ['./stock-take-details.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
})
export class StockTakeDetailsComponent implements OnInit {
  session: StockTakeSession | null = null;
  loading = false;
  activeDetailTab: 'entries' | 'comparison' = 'entries';
  selectedStockTakeType: string = '';

  // For Backtracing
  traceResult: any[] = [];
  selectedSku: string = '';
  traceLoading = false;

  // For Comparison
  comparisonData: any[] = [];
  comparisonLoading = false;

  // SKU search + inline qty edit
  entrySearch = '';
  editingEntryId: string | null = null;
  editQty: number | null = null;
  savingQty = false;

  // Entries are paged SERVER-side (sessions can hold thousands of lines);
  // the search box also filters server-side on this tab.
  entries: any[] = [];
  entriesTotal = 0;
  entriesPage = 1;
  entriesLimit = 50;
  entriesLoading = false;
  private searchTimer: any = null;

  // SOH comparison is computed in one shot server-side; page it CLIENT-side.
  compPage = 1;
  compLimit = 50;

  @ViewChild('traceModal') traceModal!: TemplateRef<any>;
  @ViewChild('confirmCompleteModal') confirmCompleteModal!: TemplateRef<any>;

  usersMap = new Map<string, string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stockTakeService: StockTakeService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private usersService: UsersService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadSession(id);
    }
    this.loadUsers();
  }

  loadSession(id: string) {
    this.loading = true;
    this.stockTakeService
      .getSessionById(id, {
        page: this.entriesPage,
        limit: this.entriesLimit,
        search: this.entrySearch.trim() || undefined,
      })
      .subscribe({
        next: (data: any) => {
          this.session = data;
          this.entries = data.entries || [];
          this.entriesTotal = data.entriesTotal ?? this.entries.length;
          this.loading = false;
          if (this.session!.sessionType === 'MASTER') {
            this.loadComparison(id);
          }
        },
        error: (err) => {
          console.error('Error loading session', err);
          this.toastr.error('Failed to load session details');
          this.loading = false;
        },
      });
  }

  /** Refetch only the entry page (search / pagination) — keeps the header. */
  loadEntries() {
    if (!this.session) return;
    this.entriesLoading = true;
    this.stockTakeService
      .getSessionById(this.session.id, {
        page: this.entriesPage,
        limit: this.entriesLimit,
        search: this.entrySearch.trim() || undefined,
      })
      .subscribe({
        next: (data: any) => {
          this.entries = data.entries || [];
          this.entriesTotal = data.entriesTotal ?? this.entries.length;
          this.entriesLoading = false;
        },
        error: (err) => {
          console.error('Error loading entries', err);
          this.entriesLoading = false;
        },
      });
  }

  get entriesPageCount(): number {
    return Math.max(1, Math.ceil(this.entriesTotal / this.entriesLimit));
  }

  setEntriesPage(page: number) {
    if (page < 1 || page > this.entriesPageCount) return;
    this.entriesPage = page;
    this.loadEntries();
  }

  /** Debounced search: server-side for entries, client-side for comparison. */
  onSearchChange() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.compPage = 1;
      this.entriesPage = 1;
      this.loadEntries();
    }, 300);
  }

  clearSearch() {
    this.entrySearch = '';
    this.onSearchChange();
  }

  loadComparison(id: string) {
    this.comparisonLoading = true;
    this.stockTakeService.getComparison(id).subscribe({
      next: (data) => {
        this.comparisonData = data;
        this.comparisonLoading = false;
      },
      error: (err) => {
        console.error('Error loading comparison', err);
        this.comparisonLoading = false;
      },
    });
  }

  loadUsers() {
    this.usersService.getUsers(1, 1000).subscribe({
      next: (res: any) => {
        const users = res.data || res.users || [];
        if (Array.isArray(users)) {
          users.forEach((u: any) => {
            this.usersMap.set(u.id, u.name || `${u.firstName} ${u.lastName}`);
          });
        }
      },
      error: (err) => {
        console.error('Error loading users', err);
      },
    });
  }

  getUserName(id: string | undefined): string {
    if (!id) return '-';
    return this.usersMap.get(id) || id || 'Unknown User';
  }

  completeSession(content: any) {
    if (!this.session) return;

    this.modalService.open(content, { centered: true, size: 'md' }).result.then(
      (result) => {
        if (result === 'Complete') {
          this.stockTakeService
            .completeSession(this.session!.id, this.selectedStockTakeType)
            .subscribe({
              next: () => {
                this.toastr.success('Stock take completed successfully!');
                this.loadSession(this.session!.id); // Reload to update status
              },
              error: (err) => {
                this.toastr.error(
                  'Failed to complete session: ' +
                    (err.error?.message || err.message),
                );
              },
            });
        }
      },
      () => {},
    );
  }

  // Backtracing Logic
  openTrace(entry: any, content: any) {
    this.selectedSku = entry.sku;
    this.traceResult = [];
    this.traceLoading = true;

    this.modalService.open(content, { size: 'lg' });

    this.stockTakeService
      .getTrace(this.session!.id, entry.sku, entry.shelf)
      .subscribe({
        next: (data) => {
          this.traceResult = data.map((t) => ({
            ...t,
            user: this.getUserName(t.scannedBy),
            qty: t.quantity,
            sessionNo: t.sessionNumber,
          }));
          this.traceLoading = false;
        },
        error: (err) => {
          console.error('Error loading trace', err);
          this.toastr.error('Failed to load trace details');
          this.traceLoading = false;
        },
      });
  }

  getTraceTotal(): number {
    return this.traceResult.reduce((sum, item) => sum + item.qty, 0);
  }

  get filteredComparison(): any[] {
    const q = this.entrySearch.trim().toLowerCase();
    if (!q) return this.comparisonData;
    return this.comparisonData.filter(
      (c: any) =>
        (c.sku || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q),
    );
  }

  get compPageCount(): number {
    return Math.max(1, Math.ceil(this.filteredComparison.length / this.compLimit));
  }

  get pagedComparison(): any[] {
    const start = (this.compPage - 1) * this.compLimit;
    return this.filteredComparison.slice(start, start + this.compLimit);
  }

  setCompPage(page: number) {
    if (page < 1 || page > this.compPageCount) return;
    this.compPage = page;
  }

  get canEditQty(): boolean {
    return (
      this.session?.status === 'DRAFT' ||
      this.session?.status === 'IN_PROGRESS'
    );
  }

  startEditQty(entry: any) {
    this.editingEntryId = entry.id;
    this.editQty = entry.quantity;
  }

  cancelEditQty() {
    this.editingEntryId = null;
    this.editQty = null;
  }

  saveEditQty(entry: any) {
    if (!this.session) return;
    const qty = Number(this.editQty);
    if (!Number.isInteger(qty) || qty < 0) {
      this.toastr.warning('Enter a valid whole number quantity (0 or more).');
      return;
    }
    this.savingQty = true;
    this.stockTakeService
      .upsertSessionEntry({
        sessionId: this.session.id,
        sku: entry.sku,
        shelf: entry.shelf,
        rack: entry.rack || '',
        quantity: qty,
        description: entry.description,
        variantPricingId: entry.variantPricingId || undefined,
        deviceId: entry.deviceId || undefined,
        scannedBy: entry.scannedBy || undefined,
      })
      .subscribe({
        next: () => {
          entry.quantity = qty;
          this.savingQty = false;
          this.cancelEditQty();
          this.toastr.success('Counted quantity updated.');
          if (this.session?.sessionType === 'MASTER') {
            this.loadComparison(this.session.id);
          }
        },
        error: (err) => {
          this.savingQty = false;
          this.toastr.error(
            'Failed to update quantity: ' +
              (err.error?.message || err.message),
          );
        },
      });
  }

  downloadVarianceReport(): void {
    if (!this.session) return;
    this.stockTakeService.downloadVarianceReport(this.session.id).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `VarianceReport-${this.session?.stockTakeSessionNumber}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success('Variance report downloaded successfully!');
      },
      error: (error: any) => {
        console.error('Error downloading variance report:', error);
        this.toastr.error('Failed to download variance report.');
      },
    });
  }

  // ── Line grid helpers (Zenflow kit: header card, variance pills, totals) ──
  headerOpen = true;
  lineFilter: 'all' | 'short' | 'over' | 'match' = 'all';

  /** "Store name (CODE)" for the header chips; empty when the store is unknown. */
  get storeLabel(): string {
    const store = this.session?.store;
    if (!store) return '';
    const name = store.name || store.storeName || '';
    if (!name) return store.storeCode || '';
    return store.storeCode ? `${name} (${store.storeCode})` : name;
  }

  /** short = counted below system SOH, over = above, match = equal. */
  varianceState(item: any): 'short' | 'over' | 'match' {
    const v = Number(item?.variance || 0);
    return v < 0 ? 'short' : v > 0 ? 'over' : 'match';
  }

  /** Rows of the current comparison page after the All / Short / Over / Matching pills. */
  get visibleComparison(): { item: any; index: number }[] {
    const out: { item: any; index: number }[] = [];
    this.pagedComparison.forEach((item, index) => {
      if (this.lineFilter !== 'all' && this.varianceState(item) !== this.lineFilter) return;
      out.push({ item, index });
    });
    return out;
  }

  get pageShortCount(): number {
    return this.pagedComparison.filter((i) => this.varianceState(i) === 'short').length;
  }

  get pageOverCount(): number {
    return this.pagedComparison.filter((i) => this.varianceState(i) === 'over').length;
  }

  get pageMatchCount(): number {
    return this.pagedComparison.filter((i) => this.varianceState(i) === 'match').length;
  }

  get compLineCountLabel(): string {
    const all = this.filteredComparison.length;
    const page = this.pagedComparison.length;
    const shown = this.visibleComparison.length;
    if (!page) return `${all} ${all === 1 ? 'line' : 'lines'}`;
    if (shown !== page) return `${shown} of ${page} on this page`;
    return page === all ? `${all} ${all === 1 ? 'line' : 'lines'}` : `${page} of ${all} lines`;
  }

  get compPageCountedTotal(): number {
    return this.pagedComparison.reduce((n, i) => n + Number(i?.totalCounted || 0), 0);
  }

  get compPageSohTotal(): number {
    return this.pagedComparison.reduce((n, i) => n + Number(i?.systemSoh || 0), 0);
  }

  get compPageVarianceTotal(): number {
    return this.pagedComparison.reduce((n, i) => n + Number(i?.variance || 0), 0);
  }

  // Whole-session figures for the variance strip (comparison is loaded in one shot)
  get compShortCount(): number {
    return this.comparisonData.filter((i) => this.varianceState(i) === 'short').length;
  }

  get compOverCount(): number {
    return this.comparisonData.filter((i) => this.varianceState(i) === 'over').length;
  }

  get compMatchCount(): number {
    return this.comparisonData.filter((i) => this.varianceState(i) === 'match').length;
  }

  get compNetVariance(): number {
    return this.comparisonData.reduce((n, i) => n + Number(i?.variance || 0), 0);
  }

  get entriesPageQtyTotal(): number {
    return this.entries.reduce((n, e) => n + Number(e?.quantity || 0), 0);
  }

  clearLineFilters(): void {
    this.lineFilter = 'all';
  }
}
